"use client"

import type React from "react"

import { useState, useRef, useEffect } from "react"
import Link from "next/link"
import { motion, useReducedMotion } from "framer-motion"
import { NFTotemLogo } from "@/components/nftotem-logo"
import { usePathname } from "next/navigation"
import { useMobile } from "@/hooks/use-mobile"
import { MENU_ANIMATIONS } from "@/constants/animations"
import { debug } from "@/lib/debug"
import { HeaderConnect } from "@/components/header-connect"

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuHeight, setMenuHeight] = useState(0)
  const menuRef = useRef<HTMLDivElement>(null)
  const prefersReducedMotion = useReducedMotion()
  // Add this ref for the header container
  const headerRef = useRef<HTMLDivElement>(null)
  // Use the mobile hook to detect mobile devices
  const isMobile = useMobile()

  const pathname = usePathname()

  // Debug the current pathname
  useEffect(() => {
    debug("Current pathname:", pathname)
  }, [pathname])

  // Force re-render when pathname changes
  const [, forceUpdate] = useState({})
  useEffect(() => {
    forceUpdate({})
  }, [pathname])

  // Explicitly check exact matches for each path
  const isAboutActive = pathname === "/about"
  const isCollectionsActive = pathname === "/collections"
  const isTotemsActive = pathname === "/totems"

  // Determine if we should use the expanded click area
  // True only on mobile (but not tablet) - no more mobile header on desktop
  const isMobileNotTablet = isMobile && window.innerWidth < 768 // Only true for phones, not tablets
  const useExpandedClickArea = isMobileNotTablet

  // Log the active states for debugging
  useEffect(() => {
    debug("Active states:", {
      about: isAboutActive,
      collections: isCollectionsActive,
      totems: isTotemsActive,
      useExpandedClickArea,
    })
  }, [isAboutActive, isCollectionsActive, isTotemsActive, useExpandedClickArea])

  // This variable is no longer needed for header styling, but keeping for potential future use
  const isAboutOrCollections = pathname === "/about" || pathname === "/collections" || pathname === "/totems"
  // Show hamburger on mobile devices (< 768px)
  const showHamburger = isMobile

  // Pre-calculate menu height when it changes to avoid layout calculations during animation
  useEffect(() => {
    if (menuRef.current && isMenuOpen) {
      // Add extra padding to ensure Collections link is fully visible
      const height = menuRef.current.scrollHeight + 32
      setMenuHeight(height)
    }
  }, [isMenuOpen])

  // Dispatch menu height when it changes
  useEffect(() => {
    if (menuRef.current) {
      const height = isMenuOpen ? menuRef.current.scrollHeight + 32 : 0

      // Dispatch custom event with menu height
      const event = new CustomEvent("headerMenuHeightChange", {
        detail: { height: height },
      })
      window.dispatchEvent(event)
    }
  }, [isMenuOpen, menuHeight])

  const toggleMenu = () => {
    const newMenuState = !isMenuOpen
    setIsMenuOpen(newMenuState)

    // Dispatch custom event for other components to listen to
    const event = new CustomEvent("headerMenuToggle", {
      detail: { isOpen: newMenuState },
    })
    window.dispatchEvent(event)
  }

  // First, let's create a function to handle navigation and menu closing
  const handleNavigation = () => {
    // Close the menu if it's open
    if (isMenuOpen) {
      setIsMenuOpen(false)

      // Dispatch custom event when menu is closed via navigation
      const event = new CustomEvent("headerMenuToggle", {
        detail: { isOpen: false },
      })
      window.dispatchEvent(event)
    }
  }

  // Use simpler animations for users who prefer reduced motion
  const animationSettings = prefersReducedMotion
    ? {
        initial: { height: "72px" },
        animate: { height: isMenuOpen ? "auto" : "72px" },
        transition: { duration: 0.1 },
      }
    : {
        initial: { height: "72px" },
        animate: { height: isMenuOpen ? `${72 + menuHeight}px` : "72px" },
        transition: {
          type: "spring",
          stiffness: 500,
          damping: 30,
          mass: 1,
          bounce: 0.25,
          duration: MENU_ANIMATIONS.HEADER.openDuration,
        },
      }

  // Staggered animation for menu items
  const menuItemVariants = {
    hidden: MENU_ANIMATIONS.HEADER.itemEntrance,
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: MENU_ANIMATIONS.HEADER.openDuration,
        type: "spring",
        stiffness: 300,
        damping: 20,
        mass: 0.8,
      },
    },
  }

  // Add this useEffect to handle clicks outside the header
  useEffect(() => {
    // Function to handle clicks outside the header
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node) && isMenuOpen) {
        setIsMenuOpen(false)

        // Dispatch custom event when menu is closed
        const customEvent = new CustomEvent("headerMenuToggle", {
          detail: { isOpen: false },
        })
        window.dispatchEvent(customEvent)
      }
    }

    // Add event listener when menu is open
    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isMenuOpen])

  // Add code to dispatch an event when the header size changes
  useEffect(() => {
    // Function to dispatch the size change event
    const dispatchSizeChangeEvent = () => {
      const event = new CustomEvent("headerSizeChange")
      window.dispatchEvent(event)
    }

    // Call immediately and after a short delay to ensure accurate measurement
    dispatchSizeChangeEvent()

    // Also dispatch on window resize and orientation change
    window.addEventListener("resize", dispatchSizeChangeEvent)
    window.addEventListener("orientationchange", dispatchSizeChangeEvent)

    const timeoutId = setTimeout(dispatchSizeChangeEvent, 100)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener("resize", dispatchSizeChangeEvent)
      window.removeEventListener("orientationchange", dispatchSizeChangeEvent)
    }
  }, [isMenuOpen])

  // Update the useEffect that handles resize events to ensure hooks are called consistently

  // Ensure we're correctly identifying tablets vs mobile phones
  useEffect(() => {
    const handleResize = () => {
      // Always close menu on resize to avoid state inconsistencies
      if (isMenuOpen) {
        setIsMenuOpen(false)

        // Dispatch custom event when menu is closed
        const event = new CustomEvent("headerMenuToggle", {
          detail: { isOpen: false },
        })
        window.dispatchEvent(event)
      }
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [isMenuOpen]) // Only depend on isMenuOpen state

  // Define active link style as a variable for consistency
  const activeStyle = "!text-[#7B39ED] font-medium"

  // Handle header click - only when expanded click area should be used
  const handleHeaderClick = (e: React.MouseEvent) => {
    // Only proceed if expanded click area should be used
    if (!useExpandedClickArea) return

    // Only toggle if we're not clicking on the logo area
    const target = e.target as HTMLElement
    const isLogoClick = target.closest('a[href="/"]')
    if (!isLogoClick) {
      toggleMenu()
    }
  }

  // Handle keyboard navigation - only when expanded click area should be used
  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    // Only proceed if expanded click area should be used
    if (!useExpandedClickArea) return

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      // Only toggle if we're not focusing on the logo
      const target = e.target as HTMLElement
      const isLogoClick = target.closest('a[href="/"]')
      if (!isLogoClick) {
        toggleMenu()
      }
    }
  }

  return (
    <div className="fixed top-4 left-0 right-0 z-50 px-4 md:px-8 lg:px-12 pointer-events-none">
      <div className="w-full">
        <div
          ref={headerRef}
          data-header-container="true"
          data-desktop-menu="true"
          className="w-full max-w-full sm:max-w-none mx-auto md:mx-0 px-6 py-4 bg-violet-50/90 backdrop-blur-md rounded-3xl flex flex-col overflow-hidden will-change-[height] pointer-events-auto"
        >
          {/* Header row with logo and navigation - fixed height for consistent alignment */}
          <div
            className="w-full h-10 flex justify-between items-center"
            onClick={handleHeaderClick}
            style={{ cursor: useExpandedClickArea ? "pointer" : "default" }}
            role={useExpandedClickArea ? "button" : undefined}
            tabIndex={useExpandedClickArea ? 0 : undefined}
            aria-expanded={useExpandedClickArea ? isMenuOpen : undefined}
            aria-label={useExpandedClickArea ? "Navigation menu" : undefined}
            aria-controls={useExpandedClickArea ? "mobile-navigation-menu" : undefined}
            onKeyDown={handleHeaderKeyDown}
          >
            {/* Logo container with explicit height and flex centering */}
            <div className="flex items-center h-full">
              <Link
                href="/"
                className="relative transition-opacity hover:opacity-80 flex items-center"
                onClick={handleNavigation}
              >
                <NFTotemLogo width={132} height={32} />
              </Link>
            </div>

            {/* Desktop navigation - visible on all pages on screens > 768px */}
            <div className="hidden md:flex items-center h-full space-x-6 mx-6 flex-1 justify-center">
                <Link
                  href="/about"
                  prefetch={true}
                  className={`text-gray-950 text-md uppercase font-funnel font-medium hover:text-brand-purple transition-colors flex items-center h-full ${
                    isAboutActive ? activeStyle : ""
                  }`}
                  onClick={handleNavigation}
                  aria-current={isAboutActive ? "page" : undefined}
                  style={isAboutActive ? { color: "#7B39ED" } : {}}
                >
                  <span className="inline-block transform translate-y-[1px]">
                    About
                    {isAboutActive && <span className="sr-only"> (current page)</span>}
                  </span>
                </Link>
                <Link
                  href="/collections"
                  prefetch={true}
                  className={`text-gray-950 text-md uppercase font-funnel font-medium hover:text-brand-purple transition-colors flex items-center h-full ${
                    isCollectionsActive ? activeStyle : ""
                  }`}
                  onClick={handleNavigation}
                  aria-current={isCollectionsActive ? "page" : undefined}
                  style={isCollectionsActive ? { color: "#7B39ED" } : {}}
                >
                  <span className="inline-block transform translate-y-[1px]">
                    Collections
                    {isCollectionsActive && <span className="sr-only"> (current page)</span>}
                  </span>
                </Link>
                <Link
                  href="/totems"
                  prefetch={true}
                  className={`text-gray-950 text-md uppercase font-funnel font-medium hover:text-brand-purple transition-colors flex items-center h-full ${
                    isTotemsActive ? activeStyle : ""
                  }`}
                  onClick={handleNavigation}
                  aria-current={isTotemsActive ? "page" : undefined}
                  style={isTotemsActive ? { color: "#7B39ED" } : {}}
                >
                  <span className="inline-block transform translate-y-[1px]">
                    Totems
                    {isTotemsActive && <span className="sr-only"> (current page)</span>}
                  </span>
                </Link>
              </div>

            {/* Right side: desktop connect button - visible on all pages on screens > 768px */}
            <div className="hidden md:flex items-center h-full">
              <HeaderConnect />
            </div>


            {showHamburger && (
              <motion.div
                whileTap={{ scale: 0.95 }}
              >
                <button
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    toggleMenu()
                  }}
                  className="w-10 h-10 rounded-xl outline-offset-[-1px] inline-flex flex-col justify-center items-center"
                  aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                  aria-expanded={isMenuOpen}
                  aria-controls="mobile-navigation-menu"
                >
                  <motion.svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <motion.path
                      d="M2.29297 6.04297H17.7096"
                      stroke="#030712"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      variants={{
                        hover: { y: -2 },
                        initial: { y: 0 },
                      }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    />
                    <motion.path
                      d="M2.29297 13.9596H17.7096"
                      stroke="#030712"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      variants={{
                        hover: { y: 2 },
                        initial: { y: 0 },
                      }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    />
                  </motion.svg>
                </button>
              </motion.div>
            )}
          </div>

          {/* Menu items container - only visible when mobile menu is open */}
          <div
            ref={menuRef}
            id="mobile-navigation-menu"
            className="overflow-hidden"
            aria-hidden={!isMenuOpen}
          >
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={isMenuOpen && showHamburger ? { opacity: 1, height: "auto" } : { opacity: 0, height: 0 }}
              transition={{ 
                type: "spring", 
                stiffness: 300, 
                damping: 25,
                duration: 0.3
              }}
            >
              <motion.div
                initial="hidden"
                animate={isMenuOpen ? "visible" : "hidden"}
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.05,
                      delayChildren: 0.1,
                    },
                  },
                }}
              >
                <div className="flex flex-col items-start space-y-4 pt-6 pb-2">
                  <motion.div
                    variants={{
                      hidden: { opacity: 0, y: -20 },
                      visible: { opacity: 1, y: 0 },
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <Link
                      href="/about"
                      prefetch={true}
                      className={`text-gray-950 text-lg font-medium uppercase font-funnel leading-[40px] hover:text-brand-purple transition-colors block text-center md:text-left lg:text-left ${
                        isAboutActive ? activeStyle : ""
                      }`}
                      onClick={handleNavigation}
                      aria-current={isAboutActive ? "page" : undefined}
                      style={isAboutActive ? { color: "#7B39ED" } : {}}
                    >
                      About
                      {isAboutActive && <span className="sr-only"> (current page)</span>}
                    </Link>
                  </motion.div>
                  <motion.div
                    variants={{
                      hidden: { opacity: 0, y: -20 },
                      visible: { opacity: 1, y: 0 },
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <Link
                      href="/collections"
                      prefetch={true}
                      className={`text-gray-950 text-lg font-medium uppercase font-funnel leading-[40px] hover:text-brand-purple transition-colors block text-center md:text-left lg:text-left ${
                        isCollectionsActive ? activeStyle : ""
                      }`}
                      onClick={handleNavigation}
                      aria-current={isCollectionsActive ? "page" : undefined}
                      style={isCollectionsActive ? { color: "#7B39ED" } : {}}
                    >
                      Collections
                      {isCollectionsActive && <span className="sr-only"> (current page)</span>}
                    </Link>
                  </motion.div>
                  <motion.div
                    variants={{
                      hidden: { opacity: 0, y: -20 },
                      visible: { opacity: 1, y: 0 },
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <Link
                      href="/totems"
                      prefetch={true}
                      className={`text-gray-950 text-lg font-medium uppercase font-funnel leading-[40px] hover:text-brand-purple transition-colors block text-center md:text-left lg:text-left ${
                        isTotemsActive ? activeStyle : ""
                      }`}
                      onClick={handleNavigation}
                      aria-current={isTotemsActive ? "page" : undefined}
                      style={isTotemsActive ? { color: "#7B39ED" } : {}}
                    >
                      Totems
                      {isTotemsActive && <span className="sr-only"> (current page)</span>}
                    </Link>
                  </motion.div>

                  {/* Mobile Connect button in menu */}
                  <motion.div
                    variants={{
                      hidden: { opacity: 0, y: -20 },
                      visible: { opacity: 1, y: 0 },
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <div className="mt-6">
                      <div className="flex justify-center w-full">
                        <div className="w-full max-w-xs">
                          <HeaderConnect size="large" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}
